import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const T = {
  accent: '#3b82f6',
  border: 'rgba(0,0,0,0.07)',
  borderMid: 'rgba(0,0,0,0.12)',
  surface: '#ffffff',
  textPrimary: '#0f172a',
  textMuted: '#94a3b8',
  fontSans: "'DM Sans','Segoe UI',sans-serif",
};

function IcSearch() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
      <circle cx="6.5" cy="6.5" r="5" stroke="currentColor" strokeWidth="1.5" />
      <line x1="10.5" y1="10.5" x2="14" y2="14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

function IcChevronDown() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
      <polyline points="2,4 6,8 10,4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export default function SearchSelect({
  value,
  onChange,
  options = [],
  placeholder = 'Select…',
  searchPlaceholder = 'Search…',
  emptyMessage = 'No results found',
  height = 36,
  hasError = false,
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [dropdownPosition, setDropdownPosition] = useState(null);
  const wrapRef = useRef(null);
  const triggerRef = useRef(null);
  const dropdownRef = useRef(null);
  const searchRef = useRef(null);

  const selected = options.find((o) => o.value === value);
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return options;
    return options.filter(
      (o) => (o.label || '').toLowerCase().includes(q) || String(o.value || '').toLowerCase().includes(q),
    );
  }, [options, search]);

  const computeDropdownPosition = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return null;
    const rect = el.getBoundingClientRect();
    const gap = 4;
    const pad = 12;
    const searchBarHeight = 45;
    const preferredListHeight = 220;
    const spaceBelow = window.innerHeight - rect.bottom - gap - pad;
    const spaceAbove = rect.top - gap - pad;
    const openUp = spaceBelow < preferredListHeight + searchBarHeight && spaceAbove > spaceBelow;
    const listMaxHeight = Math.max(96, Math.min(preferredListHeight, (openUp ? spaceAbove : spaceBelow) - searchBarHeight));
    if (openUp) {
      return {
        bottom: window.innerHeight - rect.top + gap,
        left: rect.left,
        width: rect.width,
        listMaxHeight,
        placement: 'top',
      };
    }
    return {
      top: rect.bottom + gap,
      left: rect.left,
      width: rect.width,
      listMaxHeight,
      placement: 'bottom',
    };
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    const next = computeDropdownPosition();
    if (next) setDropdownPosition(next);
  }, [open, computeDropdownPosition]);

  useEffect(() => {
    if (!open) return;
    const update = () => {
      const next = computeDropdownPosition();
      if (next) setDropdownPosition(next);
    };
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [open, computeDropdownPosition]);

  useEffect(() => {
    if (!open) setDropdownPosition(null);
  }, [open]);

  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current?.contains(e.target) || dropdownRef.current?.contains(e.target)) return;
      setOpen(false);
      setSearch('');
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (open && searchRef.current) searchRef.current.focus();
    if (!open) setSearch('');
  }, [open]);

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        style={{
          display: 'flex',
          alignItems: 'center',
          width: '100%',
          height,
          padding: '0 12px',
          borderRadius: 8,
          border: `1px solid ${hasError ? '#dc2626' : open ? T.accent : T.borderMid}`,
          background: T.surface,
          cursor: 'pointer',
          outline: 'none',
          gap: 8,
          fontFamily: T.fontSans,
          boxSizing: 'border-box',
        }}
      >
        <span
          style={{
            flex: 1,
            textAlign: 'left',
            fontSize: 13,
            color: selected ? T.textPrimary : T.textMuted,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {selected?.label || placeholder}
        </span>
        <span style={{ color: T.textMuted, display: 'flex', transform: open ? 'rotate(180deg)' : 'none' }}>
          <IcChevronDown />
        </span>
      </button>
      {open && dropdownPosition && createPortal(
        <div
          ref={dropdownRef}
          style={{
            position: 'fixed',
            ...(dropdownPosition.placement === 'top'
              ? { bottom: dropdownPosition.bottom, top: 'auto' }
              : { top: dropdownPosition.top, bottom: 'auto' }),
            left: dropdownPosition.left,
            width: dropdownPosition.width,
            zIndex: 10050,
            background: T.surface,
            border: `1px solid ${T.borderMid}`,
            borderRadius: 10,
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '8px 12px',
              borderBottom: `1px solid ${T.border}`,
            }}
          >
            <span style={{ color: T.textMuted, display: 'flex' }}><IcSearch /></span>
            <input
              ref={searchRef}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={searchPlaceholder}
              style={{
                flex: 1,
                border: 'none',
                outline: 'none',
                fontSize: 13,
                color: T.textPrimary,
                background: 'transparent',
                fontFamily: T.fontSans,
              }}
            />
          </div>
          <div style={{ maxHeight: dropdownPosition.listMaxHeight, overflowY: 'auto' }}>
            {filtered.length === 0 ? (
              <div style={{ padding: '12px 14px', fontSize: 12, color: T.textMuted }}>{emptyMessage}</div>
            ) : (
              filtered.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => {
                    onChange(opt.value);
                    setOpen(false);
                    setSearch('');
                  }}
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    border: 'none',
                    borderBottom: `1px solid ${T.border}`,
                    background: opt.value === value ? '#eff6ff' : T.surface,
                    color: opt.value === value ? T.accent : T.textPrimary,
                    fontFamily: T.fontSans,
                    fontSize: 13,
                    textAlign: 'left',
                    cursor: 'pointer',
                    fontWeight: opt.value === value ? 600 : 400,
                  }}
                >
                  {opt.label}
                </button>
              ))
            )}
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}
